import { useEffect, useState } from "react";
import { CircleChevronLeft } from "lucide-react";
import toast from "react-hot-toast";
import { InverntroyService } from "../../services/api/inventroy.service";
import { ProductFormSkeleton } from "./AddProductLoading";
import { useRouter } from "@tanstack/react-router";
import DemoImage from "../../assets/images/default_product.jpg";

// Import tabs
import { BasicDetailsTab } from "./tabs/BasicDetailsTab";
import { ProductSaltTab } from "./tabs/ProductSaltTab";
import { ProductGstTab } from "./tabs/ProductGstTab";
import { ProductPackagingTab } from "./tabs/ProductPackagingTab";
import { ProductInfoTab } from "./tabs/ProductInfoTab";
import { ProductCategoryTab } from "./tabs/ProductCategoryTab";

interface AddNewProductModalProps {
  onClose: () => void;
  productData?: any;
  onSuccess: () => void;
  productId: string;
  categoryId: number;
}

export function AddNewProductModal({
  productId,
  onSuccess,
  onClose,
}: AddNewProductModalProps) {
  const router = useRouter();
  const pathname = router.state.location.pathname;

  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    "Basic Details",
    "Salts",
    "GST Details",
    "Packaging",
    "Product Info",
    "Categories",
  ];

  // Options
  const [listingCategories, setListingCategories] = useState<{ id: number, name: string }[]>([]);

  // Consolidated Form State
  const [formData, setFormData] = useState({
    // Basic
    name: "",
    description: "",
    tabletPerStripe: 1,
    categoryId: 1,
    isRxRequired: null as boolean | null,
    isPublished: null as boolean | null,
    isActive: null as boolean | null,
    mainImage: null as File | null,
    mainImagePreview: DemoImage,
    mainImageId: "",
    subImages: [null, null, null] as (File | null)[],
    subImagePreviews: [DemoImage, DemoImage, DemoImage],
    subImageIds: ["", "", ""],

    // Salts
    salts: [] as { id: string; name: string }[],

    // GST
    hsnNumber: "",
    cgst: 0,
    sgst: 0,

    // Pkgn
    packaging: "",
    packageValue: "",
    quantity: "",
    unit: "",
    productForm: "",
    qtyValue: 0,

    // Info
    introduction: "",
    howToUse: "",
    safetyAdvise: "",
    ifMiss: "",
    factBox: "",
    primaryUse: "",
    storage: "",
    commonSideEffect: "",
    alcoholInteraction: "",
    pregnancyInteraction: "",
    lactationInteraction: "",
    drivingInteraction: "",
    kidneyInteraction: "",
    liverInteraction: "",
    qAndA: "",
    howItWorks: "",
    interaction: "",
    keyIngredients: "",
    keyBenefits: "",
    source: "",
    productHighlights: "",

    // Categories
    assignedListingCategories: [] as number[],
    initialListingCategories: [] as number[],
  });

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleImageChange = (type: "main" | "sub", file: File | null, index?: number) => {
    if (type === "main") {
      setFormData((prev) => ({
        ...prev,
        mainImage: file,
        mainImagePreview: file ? URL.createObjectURL(file) : DemoImage,
      }));
    } else if (type === "sub" && typeof index === "number") {
      const newSubs = [...formData.subImages];
      newSubs[index] = file;
      const newPreviews = [...formData.subImagePreviews];
      newPreviews[index] = file ? URL.createObjectURL(file) : DemoImage;
      setFormData((prev) => ({
        ...prev,
        subImages: newSubs,
        subImagePreviews: newPreviews,
      }));
    }
  };

  const handleRemoveImage = (type: "main" | "sub", index?: number) => {
    if (type === "main") {
      setFormData((prev) => ({
        ...prev,
        mainImage: null,
        mainImagePreview: DemoImage,
        mainImageId: "",
      }));
    } else if (type === "sub" && typeof index === "number") {
      const newSubs = [...formData.subImages];
      newSubs[index] = null;
      const newPreviews = [...formData.subImagePreviews];
      newPreviews[index] = DemoImage;
      const newIds = [...formData.subImageIds];
      newIds[index] = "";
      setFormData((prev) => ({
        ...prev,
        subImages: newSubs,
        subImagePreviews: newPreviews,
        subImageIds: newIds,
      }));
    }
  };

  const toggleListingCategory = async (id: number) => {
    setFormData((prev) => ({
      ...prev,
      assignedListingCategories: prev.assignedListingCategories.includes(id)
        ? prev.assignedListingCategories.filter((c) => c !== id)
        : [...prev.assignedListingCategories, id],
    }));
  };

  // --- Load Initial Data ---

  const loadDropdowns = async () => {
    const listCatsRes = await InverntroyService.getCategoryList();
    if (listCatsRes?.success) {
      setListingCategories(Array.isArray(listCatsRes.data) ? listCatsRes.data : []);
    }
  };

  const loadProductDetails = async () => {
    if (!productId) return;
    setIsLoading(true);
    try {
      const res = await InverntroyService.getProductDetails(productId);
      if (res?.success && res.data) {
        const d = res.data;
        const media = d.media || d.productMedia || [];
        const mainImage = media.find((m: any) => m.isMain || m.isMainImage);
        const subImages = media.filter((m: any) => !m.isMain && !m.isMainImage);

        setFormData({
          name: d.productMstr?.name || d.name || "",
          description: d.productMstr?.description || d.description || "",
          tabletPerStripe: d.productMstr?.tabletPerStripe || d.tabletPerStripe || 1,
          categoryId: d.productMstr?.categoryId || d.category_id || 1,
          isRxRequired: d.productMstr?.isRxRequired ?? d.isRxRequired ?? null,
          isPublished: d.productMstr?.isPublished ?? d.isPublished ?? null,
          isActive: d.productMstr?.isActive ?? d.isActive ?? null,

          mainImage: null,
          mainImagePreview: mainImage?.productImagePath || mainImage?.product_image_path || DemoImage,
          mainImageId: mainImage?.id?.toString() || "",

          subImages: [null, null, null],
          subImagePreviews: [
            subImages[0]?.productImagePath || subImages[0]?.product_image_path || DemoImage,
            subImages[1]?.productImagePath || subImages[1]?.product_image_path || DemoImage,
            subImages[2]?.productImagePath || subImages[2]?.product_image_path || DemoImage,
          ],
          subImageIds: [
            subImages[0]?.id?.toString() || "",
            subImages[1]?.id?.toString() || "",
            subImages[2]?.id?.toString() || "",
          ],

          salts: (d.salts || d.productSalt || []).map((s: any) => ({ id: s.id, name: s.name })),

          hsnNumber: d.productDtls?.hsnNumber || d.hsnNumber || "",
          cgst: d.productDtls?.cgst || d.newCgst || d.cgst || 0,
          sgst: d.productDtls?.sgst || d.newSgst || d.sgst || 0,

          packaging: d.productPkgn?.packaging || "",
          packageValue: d.productPkgn?.package || "",
          quantity: d.productPkgn?.quantity || "",
          unit: d.productPkgn?.unit || "",
          productForm: d.productPkgn?.productForm || "",
          qtyValue: d.productPkgn?.qtyValue || 0,

          introduction: d.productInfo?.introduction || "",
          howToUse: d.productInfo?.howToUse || "",
          safetyAdvise: d.productInfo?.safetyAdvise || "",
          ifMiss: d.productInfo?.ifMiss || "",
          factBox: d.productInfo?.factBox || "",
          primaryUse: d.productInfo?.primaryUse || "",
          storage: d.productInfo?.storage || "",
          commonSideEffect: d.productInfo?.commonSideEffect || "",
          alcoholInteraction: d.productInfo?.alcoholInteraction || "",
          pregnancyInteraction: d.productInfo?.pregnancyInteraction || "",
          lactationInteraction: d.productInfo?.lactationInteraction || "",
          drivingInteraction: d.productInfo?.drivingInteraction || "",
          kidneyInteraction: d.productInfo?.kidneyInteraction || "",
          liverInteraction: d.productInfo?.liverInteraction || "",
          qAndA: d.productInfo?.qAndA || "",
          howItWorks: d.productInfo?.howItWorks || "",
          interaction: d.productInfo?.interaction || "",
          keyIngredients: d.productInfo?.keyIngredients || "",
          keyBenefits: d.productInfo?.keyBenefits || "",
          source: d.productInfo?.source || "",
          productHighlights: d.productInfo?.productHighlights || "",

          assignedListingCategories: (d.productListingCategories || []).map((c: any) => typeof c === 'object' ? c.id : c),
          initialListingCategories: (d.productListingCategories || []).map((c: any) => typeof c === 'object' ? c.id : c),
        });
      }
    } catch (error) {
      toast.error("Failed to load product details");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    if (productId) {
      loadProductDetails();
    }
  }, [productId]);


  // --- Submit Actions ---

  const urlToBlob = async (url: string): Promise<Blob> => {
    const response = await fetch(url);
    return await response.blob();
  };

  const getDefaultImageFile = async (): Promise<File> => {
    const blob = await urlToBlob(DemoImage);
    return new File([blob], "default_product.jpg", { type: "image/jpeg" });
  };

  const handleCreateProduct = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (
      formData.isRxRequired === null ||
      formData.isPublished === null ||
      formData.isActive === null
    ) {
      toast.error("Please select Rx Required, Published and Active status");
      return;
    }

    console.log("Listing category", formData.assignedListingCategories)

    setIsUpdating(true);
    try {
      const payload = new FormData();
      // Mstr
      payload.append("productMstr", JSON.stringify({
        name: formData.name,
        description: formData.description,
        categoryId: formData.categoryId,
        isPublished: formData.isPublished,
        isActive: formData.isActive,
        isRxRequired: formData.isRxRequired,
        tabletPerStripe: formData.tabletPerStripe,
      }));
      if (formData.assignedListingCategories.length > 0) {
        payload.append("listingCategoryIds", JSON.stringify(formData.assignedListingCategories));
      }
      // Salts
      if (formData.salts.length > 0) {
        payload.append("salts", JSON.stringify(formData.salts.map(s => ({ name: s.name }))));
      }
      // Pkgn
      payload.append("productPkgn", JSON.stringify({
        packaging: formData.packaging,
        package: formData.packageValue,
        quantity: formData.quantity,
        unit: formData.unit,
        productForm: formData.productForm,
        qtyValue: formData.qtyValue,
      }));
      // Info
      payload.append("productInfo", JSON.stringify({
        introduction: formData.introduction,
        howToUse: formData.howToUse,
        safetyAdvise: formData.safetyAdvise,
        ifMiss: formData.ifMiss,
        factBox: formData.factBox,
        primaryUse: formData.primaryUse,
        storage: formData.storage,
        commonSideEffect: formData.commonSideEffect,
        alcoholInteraction: formData.alcoholInteraction,
        pregnancyInteraction: formData.pregnancyInteraction,
        lactationInteraction: formData.lactationInteraction,
        drivingInteraction: formData.drivingInteraction,
        kidneyInteraction: formData.kidneyInteraction,
        liverInteraction: formData.liverInteraction,
        qAndA: formData.qAndA,
        howItWorks: formData.howItWorks,
        interaction: formData.interaction,
        keyIngredients: formData.keyIngredients,
        keyBenefits: formData.keyBenefits,
        source: formData.source,
        productHighlights: formData.productHighlights,
      }));
      // Dtls
      payload.append("productDtls", JSON.stringify({
        hsnNumber: formData.hsnNumber,
        cgst: formData.cgst,
        sgst: formData.sgst,
      }));

      if (formData.mainImage) {
        payload.append("main_image", formData.mainImage);
      } else {
        payload.append("main_image", await getDefaultImageFile());
      }

      for (let i = 0; i < 3; i++) {
        if (formData.subImages[i]) {
          payload.append(`sub_image_${i + 1}`, formData.subImages[i] as File);
        } else {
          payload.append(`sub_image_${i + 1}`, await getDefaultImageFile());
        }
      }

      const res = await InverntroyService.createProductV2(payload);
      if (res.success) {
        toast.success("Product created successfully");
        onSuccess();
        onClose();
      } else {
        toast.error(res.message || "Failed to create product");
      }
    } catch (err) {
      toast.error("An error occurred while creating product");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateBasicDetails = async () => {
    if (!productId) return;
    if (
      formData.isRxRequired === null ||
      formData.isPublished === null ||
      formData.isActive === null
    ) {
      toast.error("Please select Rx Required, Published and Active status");
      return;
    }
    setIsUpdating(true);
    try {
      const payload = new FormData();
      payload.append("productMstr", JSON.stringify({
        name: formData.name,
        description: formData.description,
        isPublished: formData.isPublished,
        isActive: formData.isActive,
        isRxRequired: formData.isRxRequired,
        tabletPerStripe: formData.tabletPerStripe,
      }));
      if (formData.salts.length > 0) {
        payload.append("salts", JSON.stringify(formData.salts.map(s => ({ id: s.id, name: s.name }))));
      }

      if (formData.mainImage) payload.append("main_image", formData.mainImage);
      if (formData.mainImageId) payload.append("mainImageId", formData.mainImageId);

      for (let i = 0; i < 3; i++) {
        if (formData.subImages[i]) payload.append(`sub_image_${i + 1}`, formData.subImages[i] as File);
        if (formData.subImageIds[i]) payload.append(`subImage${i + 1}Id`, formData.subImageIds[i]);
      }

      const res = await InverntroyService.updateProductBasicV2(productId, payload);
      if (res.success) {
        toast.success("Basic details updated");
        onSuccess();
      } else toast.error(res.message);
    } catch (err) {
      toast.error("Failed to update basic details");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateSalts = async () => {
    handleUpdateBasicDetails(); // salts are part of basic details patch
  };

  const handleUpdateGst = async () => {
    if (!productId) return;
    setIsUpdating(true);
    try {
      const res = await InverntroyService.updateProductTaxesV2(productId, {
        productDtls: { hsnNumber: formData.hsnNumber, cgst: formData.cgst, sgst: formData.sgst }
      });
      if (res.success) toast.success("Taxes updated");
      else toast.error(res.message);
    } catch (err) {
      toast.error("Failed to update taxes");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdatePackaging = async () => {
    if (!productId) return;
    setIsUpdating(true);
    try {
      const res = await InverntroyService.updateProductPkgnV2(productId, {
        productPkgn: {
          packaging: formData.packaging,
          package: formData.packageValue,
          quantity: formData.quantity,
          unit: formData.unit,
          productForm: formData.productForm,
          qtyValue: formData.qtyValue,
        }
      });
      if (res.success) toast.success("Packaging updated");
      else toast.error(res.message);
    } catch (err) {
      toast.error("Failed to update packaging");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateInfo = async () => {
    if (!productId) return;
    setIsUpdating(true);
    try {
      const res = await InverntroyService.updateProductInfoV2(productId, {
        productInfo: {
          introduction: formData.introduction,
          howToUse: formData.howToUse,
          safetyAdvise: formData.safetyAdvise,
          ifMiss: formData.ifMiss,
          factBox: formData.factBox,
          primaryUse: formData.primaryUse,
          storage: formData.storage,
          commonSideEffect: formData.commonSideEffect,
          alcoholInteraction: formData.alcoholInteraction,
          pregnancyInteraction: formData.pregnancyInteraction,
          lactationInteraction: formData.lactationInteraction,
          drivingInteraction: formData.drivingInteraction,
          kidneyInteraction: formData.kidneyInteraction,
          liverInteraction: formData.liverInteraction,
          qAndA: formData.qAndA,
          howItWorks: formData.howItWorks,
          interaction: formData.interaction,
          keyIngredients: formData.keyIngredients,
          keyBenefits: formData.keyBenefits,
          source: formData.source,
          productHighlights: formData.productHighlights,
        }
      });
      if (res.success) toast.success("Info updated");
      else toast.error(res.message);
    } catch (err) {
      toast.error("Failed to update info");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleUpdateCategory = async () => {
    if (!productId) return;
    setIsUpdating(true);
    try {
      const addIds = formData.assignedListingCategories.filter(id => !formData.initialListingCategories.includes(id));
      const removeIds = formData.initialListingCategories.filter(id => !formData.assignedListingCategories.includes(id));

      const payload: any = {};
      if (addIds.length > 0) payload.addIds = addIds;
      if (removeIds.length > 0) payload.removeIds = removeIds;

      if (Object.keys(payload).length > 0) {
        const res = await InverntroyService.updateProductListingCategoriesV2(productId, payload);
        if (res.success) {
          toast.success("Categories updated");
          setFormData(prev => ({ ...prev, initialListingCategories: prev.assignedListingCategories }));
        } else {
          toast.error(res.message);
        }
      } else {
        toast.success("No changes to categories");
      }
    } catch (err) {
      toast.error("Failed to update categories");
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <section className="w-full pb-2">
      <div className="flex items-center justify-between mb-3">
        <h1 onClick={onClose} className="flex items-center justify-start gap-2 cursor-pointer">
          {pathname !== "/admin/stocks-orders/items" && (
            <CircleChevronLeft className="w-6 h-6 bg-[#808080] text-white rounded-full " />
          )}
          <span className="font-medium text-lg text-emerald-600">
            {productId ? "Update Product" : "Add New Product"}
          </span>
          {productId && formData.name && (
            <span className="font-semibold text-lg text-gray-800">
              - {formData.name}
            </span>
          )}
        </h1>
      </div>

      <div className="border border-[#DCDCDC] w-full bg-white rounded-xl mt-3 p-4">
        {isLoading ? (
          <ProductFormSkeleton />
        ) : (
          <div className="flex flex-col gap-6">
            {/* Horizontal Tabs Header */}
            <div className="w-full flex overflow-x-auto border-b border-gray-200">
              {tabs.map((tab, idx) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(idx)}
                  className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === idx
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="flex-1 bg-white min-h-[50vh]">
              {activeTab === 0 && (
                <BasicDetailsTab
                  data={formData}
                  onChange={handleChange}
                  onImageChange={handleImageChange}
                  onRemoveImage={handleRemoveImage}
                  isEdit={!!productId}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateBasicDetails}
                />
              )}
              {activeTab === 1 && (
                <ProductSaltTab
                  salts={formData.salts}
                  onChange={(s) => handleChange("salts", s)}
                  isEdit={!!productId}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateSalts}
                />
              )}
              {activeTab === 2 && (
                <ProductGstTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={!!productId}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateGst}
                />
              )}
              {activeTab === 3 && (
                <ProductPackagingTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={!!productId}
                  isLoading={isUpdating}
                  onUpdate={handleUpdatePackaging}
                />
              )}
              {activeTab === 4 && (
                <ProductInfoTab
                  data={formData}
                  onChange={handleChange}
                  isEdit={!!productId}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateInfo}
                />
              )}
              {activeTab === 5 && (
                <ProductCategoryTab
                  assignedListingCategories={formData.assignedListingCategories}
                  listingCategories={listingCategories}
                  toggleListingCategory={toggleListingCategory}
                  isEdit={!!productId}
                  isLoading={isUpdating}
                  onUpdate={handleUpdateCategory}
                />
              )}

              {/* Next / Submit buttons for Create mode */}
              {!productId && (
                <div className="flex justify-between items-center mt-8 pt-4 border-t">
                  <button
                    type="button"
                    onClick={() => setActiveTab(prev => Math.max(0, prev - 1))}
                    disabled={activeTab === 0 || isUpdating}
                    className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  {activeTab < tabs.length - 1 ? (
                    <button
                      type="button"
                      onClick={() => setActiveTab(prev => Math.min(tabs.length - 1, prev + 1))}
                      className="px-4 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700"
                    >
                      Next
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={handleCreateProduct}
                      disabled={isUpdating}
                      className="px-6 py-2 text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-50"
                    >
                      {isUpdating ? "Creating..." : "Create Product"}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
